import { ReactNode } from 'react'
import clsx from 'clsx'
import { KTSVG } from '../../../_metronic/helpers'
import { StatusDotTone } from '../Table/TableCells'
import styles from './styles/StatCards.module.css'

export type StatCardItem = {
  label: string
  /** Valor exibido; ausente vira "—". */
  value?: ReactNode
  /** Destaca o valor (ex.: erros > 0 em vermelho). O rótulo deve explicar o significado. */
  tone?: 'default' | 'danger' | 'success'
  /** Identificador do card no modo selecionável (padrão: o rótulo). */
  id?: string | number
  /** Valor ainda carregando: mostra um marcador pulsando no lugar do número. */
  isLoading?: boolean
  /** Ícone antes do rótulo (caminho do `IconPaths`), ex.: ícone de cada estado. */
  icon?: string
  /** Cor do ícone (mesmos tons do StatusDot). Padrão: neutral. */
  iconTone?: StatusDotTone
}

type StatCardsProps = {
  items: StatCardItem[]
  /** Nome acessível do grupo (ex.: "Resumo da importação"). */
  ariaLabel?: string
  /** Card selecionado. Com `onSelect`, os cards viram botões (ex.: filtro por estado). */
  activeId?: string | number
  onSelect?: (id: string | number) => void
}

const toneClass = {
  default: styles.value,
  danger: styles.valueDanger,
  success: styles.valueSuccess,
}

/** Cor do ícone por tom — mesmas cores do StatusDot, via classes `svg-icon-*` do tema. */
const iconToneClass: Record<StatusDotTone, string> = {
  neutral: 'svg-icon-muted',
  warning: 'svg-icon-warning',
  info: 'svg-icon-info',
  primary: 'svg-icon-primary',
  success: 'svg-icon-success',
  danger: 'svg-icon-danger',
  dark: 'svg-icon-dark',
}

/** Rótulo, com o ícone (decorativo — o texto carrega o significado) quando há `icon`. */
const StatLabel = ({ item }: { item: StatCardItem }) => (
  <>
    {item.icon && (
      <span aria-hidden='true' className={styles.icon}>
        <KTSVG
          path={item.icon}
          className={iconToneClass[item.iconTone ?? 'neutral']}
          svgClassName=''
        />
      </span>
    )}
    <span className={styles.labelText}>{item.label}</span>
  </>
)

const StatValue = ({ item }: { item: StatCardItem }) =>
  item.isLoading ? (
    <span className={styles.valueLoading}>
      <span className='visually-hidden'>carregando</span>
    </span>
  ) : (
    <>{item.value ?? '—'}</>
  )

/**
 * Faixa de indicadores em cards (rótulo + número), ex.: acima de uma tabela de detalhes.
 * Renderiza uma lista de definições (<dl>): o leitor de tela anuncia cada rótulo com o valor.
 *
 * Com `onSelect`, cada card (compacto) vira um botão com `aria-pressed` — o selecionado ganha
 * borda mais grossa na cor da marca e rótulo em negrito (não só cor).
 *
 * @example
 * <StatCards items={[{ label: 'Importados', value: 120 }, { label: 'Erros', value: 3, tone: 'danger' }]} />
 * <StatCards items={estados} activeId={estado} onSelect={setEstado} ariaLabel='Estado' />
 */
export const StatCards = ({ items, ariaLabel, activeId, onSelect }: StatCardsProps) => {
  if (onSelect) {
    return (
      <div className={clsx(styles.stats, styles.statsRow)} role='group' aria-label={ariaLabel}>
        {items.map((item) => {
          const id = item.id ?? item.label
          const ativo = id === activeId
          return (
            <button
              key={id}
              type='button'
              className={clsx(styles.stat, styles.statSelectable, ativo && styles.statActive)}
              aria-pressed={ativo}
              onClick={() => onSelect(id)}
            >
              <span className={styles.label}>
                <StatLabel item={item} />
              </span>
              <span className={toneClass[item.tone ?? 'default']}>
                <StatValue item={item} />
              </span>
            </button>
          )
        })}
      </div>
    )
  }

  return (
    <dl className={styles.stats} aria-label={ariaLabel}>
      {items.map((item) => (
        <div key={item.id ?? item.label} className={styles.stat}>
          <dt className={styles.label}>
            <StatLabel item={item} />
          </dt>
          <dd className={toneClass[item.tone ?? 'default']}>
            <StatValue item={item} />
          </dd>
        </div>
      ))}
    </dl>
  )
}
